'use-strict';
import React, { useEffect, useState } from 'react';
import intersection from 'lodash/intersection';
import flatten from 'lodash/flatten';
import map from 'lodash/map';
import { useHistory } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { useCurrentUserQuery } from '@/redux/services/portal/user.api.js';
import { useLoadingState } from '@/contexts/LoadingContext';
import { useIsAuthenticated } from '@/hooks/useIsAuthenticated';
import * as config from '@/config/env.config';

/**
 * HOC that Handles whether or not the user is allowed to see the page.
 */
export const RouteAuthorization = ({
  allowedPermissions,
  WrappedComponent,
  ...props
}) => {
	const history = useHistory();
	const isAuthenticated = useIsAuthenticated();
	const { isLoading: auth0IsLoading } = useAuth0();
	const [shouldRedirect, setShouldRedirect] = useState(false);
	const [redirectReason, setRedirectReason] = useState(null);
	
	// Fetch user data only if authenticated (trust Auth0 SDK state as source of truth)
	const { data: user, isLoading: userIsLoading, error } = useCurrentUserQuery('', {
		skip: !isAuthenticated || auth0IsLoading
	});
	
	// Register loading state with global loading context
	useLoadingState('route-authorization', userIsLoading || auth0IsLoading);
	
	// Handle redirects (must be in useEffect, not render)
	useEffect(() => {
		if (shouldRedirect && redirectReason) {
			const redirectInfo = {
				reason: redirectReason,
				pathname: props.location.pathname,
				isAuthenticated,
				user: user ? { id: user.id, email: user.email, roles: user.roles?.length } : null,
				error: error ? { message: error.message, status: error.status } : null
			};
			console.error('❌ RouteAuthorization: Redirecting to security check', redirectInfo);
			
			if (config.ENABLE_SECURITY_ALERT) {
				alert('About to redirect to /securitycheck\n\nState:\n' + JSON.stringify(redirectInfo, null, 2) + '\n\nCheck console and state before clicking OK');
			}
			
			history.replace({
				pathname: "/securitycheck",
				state: { from: props.location }
			});
		}
	}, [shouldRedirect, redirectReason, history, props.location, isAuthenticated, user, error]);
	
	// Show loading while Auth0 or user data is being fetched
	if (auth0IsLoading || userIsLoading) {
		return null; // GlobalLoading will handle the spinner
	}
  
  // If authenticated but got an error fetching user data, redirect to security check
  // This typically means 401/403 from the API (handled by baseQueryWithAuth, but this is a fallback)
  if (error && isAuthenticated && !shouldRedirect) {
		console.error('❌ RouteAuthorization: Error fetching user data', {
			error: error.message || error,
			pathname: props.location.pathname,
			isAuthenticated
		});
		
		setRedirectReason('Error: ' + (error.message || error));
		setShouldRedirect(true);
		return null; // Wait for useEffect to handle redirect
	}

  /* istanbul ignore next */
  // If authenticated but no user data after loading completes, redirect to security check
  // But only if we're not in the middle of logout
  if (isAuthenticated && !user && !userIsLoading && !shouldRedirect) {
    const logoutInProgress = localStorage.getItem('logout_in_progress');
    if (logoutInProgress !== 'true') {
      console.warn('⚠️ RouteAuthorization: Authenticated but no user data', {
        pathname: props.location.pathname,
        isAuthenticated
      });
      
      setRedirectReason('Authenticated but no user data');
      setShouldRedirect(true);
      return null; // Wait for useEffect to handle redirect
    }
  }

  if (!user && !shouldRedirect) {
    // Check if we just logged out - don't redirect during logout
    // Let Auth0 handle the redirect, just show loading state
    const logoutInProgress = localStorage.getItem('logout_in_progress');
    if (logoutInProgress === 'true') {
      // Don't redirect - just show loading and let Auth0 handle the redirect
      // GlobalLoading will handle the spinner
      return null;
    }
    
    // Only redirect if we're truly not authenticated
    // If Auth0 says we're authenticated but user data isn't loaded yet, wait
    if (!isAuthenticated) {
      console.warn('⚠️ RouteAuthorization: Not authenticated', {
        pathname: props.location.pathname,
        isAuthenticated,
        hasUser: !!user
      });
      
      setRedirectReason('User is not authenticated');
      setShouldRedirect(true);
      return null; // Wait for useEffect to handle redirect
    } else {
      // Auth0 says we're authenticated but no user data yet - wait a bit
      console.log('⏳ RouteAuthorization: Authenticated but waiting for user data', {
        pathname: props.location.pathname,
        isAuthenticated
      });
      return null; // Show loading
    }
  }
  
  // If redirect is pending, show nothing (useEffect will handle it)
  if (shouldRedirect) {
    return null;
  }
  
  if (!user || !user.roles) {
    return <h1>Access Permissions Not Found!</h1>;
  }
  const permissionArray = flatten(map(user.roles, 'permissions'));

  if (intersection(allowedPermissions, permissionArray).length > 0 ) {
    return <WrappedComponent {...props} />;
  } else {
    return <h1>Access Permissions Not Found</h1>;
  }
};

export default RouteAuthorization;
